// src/components/Pricing.js
"use client"

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { useNavigate } from "react-router-dom";
import { Check, TrendingUp, BarChart2, PieChart, LineChart, CandlestickChart, Briefcase } from "lucide-react";
import HomeNavbar from "./HomeNavbar";
import Footer from "./Footer";



const plans = [
  {
    name: "Starter",
    price: "₹ 999",
    period: "Per Month",
    features: [
      "5 Copying Accounts",
      "Free Main Account",
      "Choose Your Trade Size",
      "Use Multiple Brokers",
      "24/7 Help",
    ],
    cta: "Start Free Trial",
    icon: TrendingUp,
  },
  {
    name: "Basic",
    price: "₹ 1,999",
    period: "Per Month",
    features: [
      "15 Copying Accounts",
      "Free Main Account",
      "Choose Your Trade Size",
      "Use Multiple Brokers",
      "24/7 Help",
    ],
    cta: "Start Free Trial",
    icon: BarChart2,
  },
  {
    name: "Standard",
    price: "₹ 3,999",
    period: "Per Month",
    features: [
      "30 Copying Accounts",
      "Free Main Account",
      "Choose Your Trade Size",
      "Use Multiple Brokers",
      "24/7 Priority Help",
    ],
    cta: "Start Free Trial",
    icon: PieChart,
  },
  {
    name: "Pro",
    price: "₹ 7,999",
    period: "Per Month",
    features: [
      "60 Copying Accounts",
      "Free Main Accounts",
      "Advanced Trade Settings",
      "Use Multiple Brokers",
      "VIP Support",
    ],
    cta: "Start Free Trial",
    icon: LineChart,
  },
  {
    name: "Elite",
    price: "₹ 14,999",
    period: "Per Month",
    features: [
      "100 Copying Accounts",
      "Free Main Accounts",
      "Custom Trade Strategies",
      "Priority Trade Execution",
      "Dedicated Account Manager",
    ],
    cta: "Start Free Trial",
    icon: CandlestickChart,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "Contact Us for Pricing",
    features: [
      "Unlimited Copying Accounts",
      "Main Accounts",
      "Your Own Login Page",
      "API Access",
      "Custom Solutions",
    ],
    cta: "Contact Our Team",
    icon: Briefcase,
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const plansRef = useRef(null);
  const includesRef = useRef(null);
  const backgroundRef = useRef(null);
  const chartRef = useRef(null);
  const candlesRef = useRef(null);

  const handleCtaClick = () => {
    navigate("/auth/login"); // Redirect to login page
  };

  useEffect(() => {
    // Background animations (simplified for mobile performance)
    const tl = gsap.timeline({ repeat: -1 });
    gsap.set(".chart-line", {
      stroke: "rgba(59, 130, 246, 0.2)",
      strokeDasharray: 1000,
      strokeDashoffset: 1000,
    });
    gsap.set(".candle", {
      scaleY: 0,
      transformOrigin: "bottom",
    });
    tl.to(".chart-line", {
      strokeDashoffset: 0,
      duration: 2,
      stagger: 0.1,
      ease: "power1.inOut",
    });
    tl.to(
      ".candle",
      {
        scaleY: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: "back.out(1.7)",
      },
      "-=1.5"
    );
    gsap.to(".bg-pattern", {
      x: "10%",
      y: "5%",
      duration: 20,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    });

    // Header animation
    const headerChildren = headerRef.current?.children || [];
    gsap.set(headerChildren, { y: 50, opacity: 0 });
    gsap.to(headerChildren, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      stagger: 0.2,
      ease: "power3.out",
      delay: 0.5,
    });

    // Plans animation
    const plansElement = plansRef.current;
    const planCards = plansElement?.querySelectorAll(".plan-card") || [];
    gsap.set(planCards, { y: 50, opacity: 0 });
    const plansObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(planCards, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            });
            plansObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 } // Lowered threshold for mobile
    );
    if (plansElement) plansObserver.observe(plansElement);

    // Includes animation
    const includesElement = includesRef.current;
    const includesChildren = includesElement?.children || [];
    gsap.set(includesChildren, { y: 50, opacity: 0 });
    const includesObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(includesChildren, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            });
            includesObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 } // Lowered threshold for mobile
    );
    if (includesElement) includesObserver.observe(includesElement);

    return () => {
      tl.kill();
      gsap.killTweensOf(".bg-pattern");
      if (plansElement) plansObserver.unobserve(plansElement);
      if (includesElement) includesObserver.unobserve(includesElement);
    };
  }, []);

  return (
    <div className="bg-slate-900 text-white">
      <HomeNavbar />

      {/* SVG Background */}
      <div ref={backgroundRef} className="fixed inset-0 opacity-10 pointer-events-none bg-pattern">
        <div ref={chartRef} className="absolute inset-0">
          <svg width="100%" height="100%" viewBox="0 0 1000 1000" preserveAspectRatio="none">
            {[...Array(10)].map((_, i) => {
              // Reduced number of lines for mobile performance
              const y1 = Number((800 - i * 30 + Math.sin(i) * 50).toFixed(2)) || 0;
              const x2 = Number((250 + i * 20).toFixed(2)) || 0;
              const y2 = Number((700 - Math.cos(i) * 100).toFixed(2)) || 0;
              const x3 = Number((500 - i * 10).toFixed(2)) || 0;
              const y3 = Number((600 + Math.sin(i) * 80).toFixed(2)) || 0;
              const x4 = Number((750 + i * 5).toFixed(2)) || 0;
              const y4 = Number((500 - Math.cos(i) * 60).toFixed(2)) || 0;
              const y5 = Number((400 + Math.sin(i) * 100).toFixed(2)) || 0;

              const coordinates = [y1, x2, y2, x3, y3, x4, y4, y5];
              if (!coordinates.every((coord) => Number.isFinite(coord))) {
                console.error(`Invalid coordinates for path ${i}:`, { y1, x2, y2, x3, y3, x4, y4, y5 });
                return null;
              }

              const d = `M 0 ${y1} C ${x2} ${y2} ${x3} ${y3} ${x4} ${y4} L 1000 ${y5}`;

              return (
                <path
                  key={`line-${i}`}
                  className="chart-line"
                  d={d}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              );
            })}
          </svg>
        </div>
        <div ref={candlesRef} className="absolute inset-0">
          <svg width="100%" height="100%" viewBox="0 0 1000 500">
            {[...Array(15)].map((_, i) => {
              // Reduced number of candles for mobile performance
              const x = 100 + i * 30;
              const height = 20 + Math.random() * 80;
              const width = 8;
              const isUp = Math.random() > 0.5;
              const color = isUp ? "rgba(16, 185, 129, 0.7)" : "rgba(239, 68, 68, 0.7)";
              const wickHeight = height * 0.6;
              return (
                <g key={`candle-${i}`}>
                  <line
                    x1={x}
                    y1={250 - wickHeight}
                    x2={x}
                    y2={250 + wickHeight}
                    stroke={color}
                    strokeWidth="1"
                  />
                  <rect
                    className="candle"
                    x={x - width / 2}
                    y={isUp ? 250 - height : 250}
                    width={width}
                    height={height}
                    fill={color}
                    rx="1"
                  />
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-12 sm:pb-20">
        {/* Header */}
        <div ref={headerRef} className="mx-auto max-w-2xl text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Choose Your Algosoftware Trading Plan
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-6"></div>
          <p className="text-lg sm:text-xl text-slate-300 leading-7 sm:leading-8">
            Select the perfect plan to boost your algo trading. Follow top traders, copy their moves, and grow your
            investments.
          </p>
        </div>

        {/* Plans */}
        <div ref={plansRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className="plan-card flex flex-col justify-between bg-slate-800 rounded-xl p-6 sm:p-8 shadow-lg hover:bg-slate-700 transition-colors"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white">{plan.name}</h3>
                  <plan.icon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
                </div>
                <div className="mt-4 flex items-baseline text-4xl sm:text-5xl font-bold tracking-tight text-white">
                  {plan.price}
                  <span className="text-base sm:text-lg font-semibold text-slate-400 ml-2">{plan.period}</span>
                </div>
                <ul className="mt-8 sm:mt-10 space-y-3 sm:space-y-4 text-sm sm:text-base text-slate-300">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-x-3 items-center">
                      <Check className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={handleCtaClick}
                className="mt-6 sm:mt-8 block rounded-md px-3 sm:px-3.5 py-2 sm:py-2.5 text-sm sm:text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900"
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* All Plans Include */}
        <div ref={includesRef} className="mt-12 sm:mt-16 mx-auto max-w-2xl text-center">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-white mb-4">
            All Plans Include
          </h3>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-6"></div>
          <ul className="text-left inline-block text-slate-300 text-sm sm:text-base">
            {[
              "Strong Security",
              "Live Trading Updates",
              "Easy-to-Use Mobile App",
              "Daily Performance Reports",
              "Trade Many Types of Assets",
            ].map((feature) => (
              <li key={feature} className="flex items-center gap-x-3 mb-2">
                <Check className="h-5 w-5 text-blue-500" aria-hidden="true" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
}